import { Share2, MoreHorizontal } from "lucide-react";
import toast from "react-hot-toast";
import { formatRelativeDate } from "../../helpers/helpers";
import { useState } from "react";

const CommentCard = ({
  rev,
  currentUserId,
  isAuthenticated,
  blog,
  onToggleLike,
  onReply,
  onEdit,
  onDelete,
}) => {
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editText, setEditText] = useState("");

  const isLiked = rev.likes?.some(
    (u) => u._id?.toString() === currentUserId?.toString()
  );

  const handleShareComment = () => {
    if (typeof window === "undefined") return;

    const url = `${window.location.href}#comment-${rev._id}`;
    navigator.clipboard.writeText(url);
    toast.success("Comment link copied!");
  };

  return (
    <div id={`comment-${rev._id}`} className="flex gap-3">
      {/* Left column: Avatar */}
      <img
        src={
          rev.user?.avatar?.url ||
          `https://ui-avatars.com/api/?name=${encodeURIComponent(
            rev.user?.name || "A"
          )}&background=e7e5e4&color=78716c&size=80`
        }
        alt={rev.user?.name || "User"}
        className="w-10 h-10 rounded-full object-cover shrink-0 mt-0.5"
      />

      {/* Right column: All content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-stone-900 font-sourcesans">
              {rev.user?.name || "Anonymous"}
            </span>
            <span className="text-xs text-stone-400 font-sourcesans">
              {formatRelativeDate(rev.createdAt)}
            </span>
          </div>
        </div>

        {rev.likes?.length > 0 && (
          <div className="flex items-center gap-1 mb-2">
            <span className="text-red-500 text-xs">❤</span>
            <span className="text-xs text-stone-500 bg-stone-100 rounded-full px-2 py-0.5 font-sourcesans">
              Liked by {rev.likes[0]?.name}
              {rev.likes.length > 1
                ? ` and ${rev.likes.length - 1} other${
                    rev.likes.length > 2 ? "s" : ""
                  }`
                : ""}
            </span>
          </div>
        )}

        <p
          className="text-sm text-stone-800 font-sourcesans leading-relaxed mb-3"
          style={{ textWrap: "pretty" }}
        >
          {rev.blogcomment}
        </p>

        <div className="flex items-center gap-5 text-xs text-stone-400 font-sourcesans tracking-wide">
          <button
            onClick={() => onToggleLike(rev._id)}
            disabled={!isAuthenticated}
            className={`flex items-center gap-1.5 uppercase tracking-widest transition
                ${isLiked ? "text-[#BC3F4A]" : "hover:text-stone-700"}
                ${!isAuthenticated ? "opacity-40 cursor-not-allowed" : ""}`}
          >
            {isLiked ? "♥" : "♡"} Like ({rev.likes?.length || 0})
          </button>

          {isAuthenticated && (
            <button
              onClick={() => setReplying((p) => !p)}
              className="flex items-center gap-1.5 uppercase tracking-widest hover:text-stone-700 transition"
            >
              💬 Reply ({rev.replies?.length || 0})
            </button>
          )}

          <button
            onClick={handleShareComment}
            className="ml-auto flex items-center gap-1.5 uppercase tracking-widest hover:text-stone-700 transition"
          >
            <Share2 size={12} /> Share
          </button>
        </div>

        {replying && (
          <div className="mt-3 flex gap-3">
            <div className="flex-1 space-y-2">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 focus:outline-none focus:border-stone-400 resize-none font-sourcesans"
                rows={3}
                placeholder="Write a reply…"
                autoFocus
              />
              <div className="flex items-center gap-2 justify-end">
                <button
                  onClick={() => {
                    setReplying(false);
                    setReplyText("");
                  }}
                  className="text-xs text-stone-400 hover:text-stone-600 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!replyText.trim()) return;
                    onReply(rev._id, replyText);
                    setReplyText("");
                    setReplying(false);
                  }}
                  className="text-xs bg-[#BC3F4A] hover:bg-[#a3323b] text-white px-4 py-1.5 rounded-full transition"
                >
                  Post
                </button>
              </div>
            </div>
          </div>
        )}

        {rev.replies?.length > 0 && (
          <div className="mt-4 pl-3 border-l-2 border-stone-200 space-y-4">
            {rev.replies.map((reply) => {
              const isReplyOwner =
                reply.user?._id?.toString() === currentUserId?.toString();
              const isAuthor =
                reply.user?._id?.toString() === blog?.user?.toString();

              return (
                <div key={reply._id} className="flex gap-3">
                  <img
                    src={
                      reply.user?.avatar?.url ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        reply.user?.name || "A"
                      )}&background=e7e5e4&color=78716c&size=64`
                    }
                    alt={reply.user?.name || "User"}
                    className="w-8 h-8 rounded-full object-cover shrink-0 mt-0.5"
                  />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold text-stone-900 font-sourcesans">
                          {reply.user?.name || "Anonymous"}
                        </span>

                        {isAuthor && (
                          <span className="text-[10px] border border-orange-400 text-orange-500 rounded px-1.5 py-0.5 font-sourcesans leading-none">
                            Author
                          </span>
                        )}
                        <span className="text-xs text-stone-400 font-sourcesans">
                          {formatRelativeDate(reply.createdAt)}
                        </span>
                      </div>

                      {isReplyOwner && editingReplyId !== reply._id && (
                        <div className="flex gap-3">
                          <button
                            onClick={() => {
                              setEditingReplyId(reply._id);
                              setEditText(reply.text);
                            }}
                            className="text-xs text-stone-400 hover:text-stone-700 transition"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => onDelete(rev._id, reply._id)}
                            className="text-xs text-stone-400 hover:text-red-500 transition"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>

                    {editingReplyId === reply._id ? (
                      <div className="space-y-2">
                        <textarea
                          className="w-full border border-stone-200 rounded-xl p-2.5 text-sm focus:outline-none focus:border-stone-400 resize-none font-sourcesans"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={2}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              onEdit(rev._id, reply._id, editText);
                              setEditingReplyId(null);
                            }}
                            className="text-xs bg-[#BC3F4A] text-white px-3 py-1 rounded-full transition"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingReplyId(null)}
                            className="text-xs text-stone-400 hover:text-stone-600 transition"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p
                        className="text-sm text-stone-800 font-sourcesans leading-relaxed mb-2"
                        style={{ textWrap: "pretty" }}
                      >
                        {reply.text}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentCard;
